import { makeObservable, observable, action, runInAction } from 'mobx'
import type Template from './Template'
import { v4 as uuidv4 } from 'uuid'
import Store from './Store'

export default class TemplateItem {
  id: string
  name: string = ''
  description?: string
  timestamp: number = 0
  position: number = 0
  descriptionFilename?: string
  descriptionFiletype?: string
  requiresMedia: boolean = false
  sectionId: string | undefined
  descriptionFileUrl: string | undefined
  template: Template
  store: Store

  deleted: boolean = false
  saving: boolean = false
  lastError?: string

  constructor(store: Store, template: Template, name: string) {
    makeObservable(this, {
      id: false,
      name: observable,
      position: observable,
      requiresMedia: observable,
      fromJSON: action,
      setName: action,
      setLastError: action,
      setDeleted: action,
      setSaving: action,
    })
    this.store = store
    this.template = template
    this.id = uuidv4()
    this.name = name
    this.timestamp = Date.now()
  }

  setName = (name: string) => {
    this.name = name
    this.handleUpdate()
  }

  asJSON() {
    return { id: this.id, name: this.name, timestamp: this.timestamp }
  }

  fromJSON(json: string | object) {
    const {
      id,
      name,
      description,
      descriptionFilename,
      descriptionFiletype,
      position,
      section_id,
      requiresMedia,
    } = typeof json === 'object' ? json : JSON.parse(json)
    console.log(json)
    if (id) this.id = id
    if (name) this.name = name
    if (position) this.position = position
    if (description) this.description = description
    if (descriptionFilename) this.descriptionFilename = descriptionFilename
    if (descriptionFiletype) this.descriptionFiletype = descriptionFiletype
    if (section_id) this.sectionId = section_id
    if (typeof requiresMedia === 'boolean') {
      this.requiresMedia = requiresMedia
    }
  }

  setLastError(value: string | undefined) {
    this.lastError = value
  }

  setSaving(value: boolean) {
    this.saving = value
  }

  setDeleted(value: boolean) {
    this.deleted = value
  }

  handleCreate = async () => {
    try {
      const item = await this.store.provider.createTemplateItem(
        this.template.id,
        {
          id: this.id,
          name: this.name,
          sectionId: this.sectionId,
          description: this.description,
          descriptionFilename: this.descriptionFilename,
          descriptionFiletype: this.descriptionFiletype,
          requiresMedia: this.requiresMedia,
        }
      )
      console.log(item)
      return this.fromJSON(item)
    } catch (e) {
      this.setLastError('Failed to add item')
      throw e
    } finally {
      this.setSaving(false)
    }
  }

  delete = () => {
    this.template.removeItem(this.id)
  }

  handleDelete = async () => {
    this.setLastError(undefined)
    this.setDeleted(true)
    this.store.provider
      .deleteTemplateItem(this.template.id, this.id)
      .catch((e) => {
        // undelete
        this.setDeleted(false)
        this.setLastError('Failed to delete')
        throw e
      })
  }

  handleUpdate = async () => {
    this.setSaving(true)
    this.setLastError(undefined)
    this.store.provider
      .updateTemplateItem(this.template.id, this.id, {
        name: this.name,
        description: this.description,
        requiresMedia: this.requiresMedia,
      })
      .then((item) => {
        this.fromJSON(item)
      })
      .catch((e) => {
        this.setLastError('Failed to update')
        throw e
      })
      .finally(() => {
        this.setSaving(false)
      })
  }

  getUploadUrl = async (filename: string) => {
    return this.store.provider.getChecklistItemUploadUrl(
      this.template.id,
      this.id,
      { filename }
    )
  }

  async update(payload: {
    name?: string
    description?: string
    requiresMedia?: boolean
  }) {
    if (payload.name) this.name = payload.name
    if (payload.description) this.description = payload.description
    if (payload.requiresMedia !== undefined) {
      this.requiresMedia = payload.requiresMedia
    }
    if (Object.keys(payload).length === 0) return
    return this.store.provider.updateTemplateItem(
      this.template.id,
      this.id,
      payload
    )
  }

  addDescriptionFile = async (filename: string, filetype: string) => {
    await this.store.provider.updateTemplateItem(this.template.id, this.id, {
      descriptionFilename: filename,
      descriptionFiletype: filetype,
    })
    this.descriptionFilename = filename
    this.descriptionFiletype = filetype
  }

  getDescriptionFileUrl = async () => {
    const data = await this.store.provider.getTemplateItemDescriptionFileUrl(
      this.template.id,
      this.id
    )

    runInAction(() => {
      this.descriptionFileUrl = data.url
    })

    return data
  }
}
